import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environment/environment';
import { AllQueryFilterResponse, LLMFilterResponse, SaveUserFilterResponse } from '../../models/filter-payload.model';
import { QueryFilter } from '../../models/filter-condition.model';
import { RenameFilter } from '../../models/filter-condition.model';
import { ConfigurableColumns, SaveSelectedColumnResponse, SelectedColumn } from '../../models/user-info.model';

export interface FilterCriteriaRequest {
  projectId: string;
  bl: string;
  commonTemplate: string;
  comments: string;
  rootGroup: string;
}

export interface FilterCriteriaResponse {
  name: string;
  filter: string;
  filterType: string;
  email: string;
  comments: string;
  versionNo: string;
  globalFilter: string;
  projectId: string
}
@Injectable({
  providedIn: 'root'
})
export class FilterCriteriaService {
  private queryAPIURL = environment.backendApiConfiguration.api_url + 'filterquery/filter';
  private apiUrl = environment.backendApiConfiguration.api_url ;

  constructor(private http: HttpClient) {}

  saveFilterCriteria(request: FilterCriteriaRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}project/filterCriteria`, request, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  getFilterCriteria(selectedGUID: string): Observable<FilterCriteriaResponse> {
    return this.http.get<FilterCriteriaResponse>(`${this.apiUrl}filterquery/filterdetails/${selectedGUID}`);
  }

  saveUserFilter(filter: QueryFilter): Observable<SaveUserFilterResponse> {
    return this.http.post<SaveUserFilterResponse>(`${this.apiUrl}FilterQuery/filter`, filter);
  }

  checkFilterName(name: string, filterType: string): Observable<any> {
    const params = { name: name, filterType: filterType };
    return this.http.get(`${this.apiUrl}FilterQuery/checkfiltername`, { params });
  }

  getAllFilterQueries(): Observable<AllQueryFilterResponse> {
    return this.http.get<AllQueryFilterResponse>(`${this.queryAPIURL}`);
  }

  renameFilter(renameData: RenameFilter): Observable<string> {
    const url = `${this.apiUrl}FilterQuery/renamefilter`;
    return this.http.patch(
      url,
      renameData,
      { responseType: 'text' as const }
    );
  }

  deleteFilter(guid: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}FilterQuery/filter/${guid}`);
  }

  updateUserFilter(guid: string, filter: QueryFilter): Observable<any> {
    return this.http.put(`${this.apiUrl}FilterQuery/filter/${guid}`, filter);
  }

  deleteFilters(guids: string[]): Observable<any> {
  return this.http.delete(`${this.apiUrl}FilterQuery/filters`, { body: guids });
  }

  createManualQueryLLM(queryText: string): Observable<LLMFilterResponse> {
    return this.http.post<LLMFilterResponse>(`${this.apiUrl}llm/chat`, { text: queryText });
  }
  
  updateFilterComments(guid: string, comments: string): Observable<any> {
    return this.http.put(`${this.apiUrl}filterquery/updatecomments/${guid}`, `"${comments}"`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  viewConfigurableColumns(): Observable<ConfigurableColumns> {
    return this.http.get<ConfigurableColumns>(`${this.apiUrl}admin/columns`);
  }

  saveSelectedColumns(selectedColumns: SelectedColumn): Observable<SaveSelectedColumnResponse> {
    return this.http.post<SaveSelectedColumnResponse>(`${this.apiUrl}admin/configuration`, selectedColumns);
  }

  updateCurrentVersion(guid: string, filter: QueryFilter): Observable<any> {
    return this.http.put(`${this.apiUrl}FilterQuery/updatecurrentversion/${guid}`, filter);
  }

  saveAsNewVersion(guid: string, filter: QueryFilter): Observable<any> {
    return this.http.put(`${this.apiUrl}FilterQuery/filter/${guid}`, filter);
  }

  getVersionHistory(guid: string): Observable<any[]> {
  const url = `${this.apiUrl}filterquery/version/${guid}`;
  return this.http.get<any[]>(url);
}

  updateVersionComment(guid: string, versionNo: string, comments: string): Observable<any> {
    return this.http.put(`${this.apiUrl}filterquery/updateversioncomment/${guid}/${versionNo}`, `"${comments}"`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }
  deleteVersion(guid: string, versionNo: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}filterquery/version/${guid}/${versionNo}`);
  }

   setDefaultFilter(guid: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}filterquery/set-default/${guid}`, {});
  }

  getDefaultFilter(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}filterquery/default`);
  }

  getAllQuery(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}filterquery/GetAllQuery`);
  }
  getColumnConfiguration(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}config/columns`);
  }
  saveColumnConfiguration(columnConfig: any): Observable<any> {
    return this.http.post(`${this.apiUrl}config/columns`, columnConfig, { responseType: 'text' as const });
  }

   getIVKDescription(matNos: string[]): Observable<{ id: string; name: string }[]> {
    return this.http.post<{ id: string; name: string }[]>(`${this.apiUrl}filter/ivk`, matNos);
  }

  setFavorite(guid: string, isFavorite: boolean): Observable<any> {
    const favoriteFlag = isFavorite ? 1 : 0;
    return this.http.post(`${this.apiUrl}filterquery/favourites/${guid}`, favoriteFlag);
  }
}

